/** Een melding van de backend dat iets gewijzigd is (`apps/backend/src/lib/realtime.ts`). */
export interface RealtimeNotice {
  /** Ontbreekt bij een melding die alleen gebieden noemt. */
  entity?: string
  id?: string | number
  /** Het pad waaronder `id` een record aanwijst; zonder is dat `entity`. */
  scope?: string
  /** Andere gebieden die dezelfde wijziging raakte: daar veroudert alles. */
  also?: string[]
}

const startsWith = (path: readonly unknown[], prefix: readonly string[]) =>
  prefix.every((segment, i) => path[i] === segment)

function mentions(value: unknown, id: string): boolean {
  if (value == null) return false
  if (typeof value === 'string' || typeof value === 'number') return String(value) === id
  if (Array.isArray(value)) return value.some((v) => mentions(v, id))
  if (typeof value === 'object') return Object.values(value).some((v) => mentions(v, id))
  return false
}

/**
 * Of een query in de cache door deze melding verouderd is. Alles onder het gebied telt, op één
 * uitzondering na: een ANDER record van dezelfde soort (een query met een eigen `id` waarin het
 * gewijzigde record niet voorkomt) blijft met rust. Lijsten en tellingen verouderen wel: daar
 * staat het gewijzigde record ook in.
 */
export function noticeTouchesQuery(notice: RealtimeNotice, queryKey: readonly unknown[]): boolean {
  const path = queryKey[0]
  if (!Array.isArray(path) || !notice.entity) return false
  if (!startsWith(path, notice.entity.split('.'))) return false
  if (notice.id === undefined) return true
  if (!startsWith(path, (notice.scope ?? notice.entity).split('.'))) return true
  const input = (queryKey[1] as { input?: unknown } | undefined)?.input
  if (mentions(input, String(notice.id))) return true
  const own = (input as { id?: unknown } | null | undefined)?.id
  return !(typeof own === 'string' || typeof own === 'number')
}

/**
 * Voert `fire(key)` hooguit eens per `ms` uit per sleutel: de eerste keer meteen, en als er
 * binnen het venster meer komen daarna nog één keer. Een gebiedsmelding ververst alles van een
 * module op het scherm; bij een import of een drukke tenant komen er tientallen per seconde.
 */
export function throttleByKey(fire: (key: string) => void, ms: number): (key: string) => void {
  const state = new Map<string, { last: number; timer: ReturnType<typeof setTimeout> | null }>()
  return (key) => {
    const now = Date.now()
    const entry = state.get(key) ?? { last: 0, timer: null }
    state.set(key, entry)
    if (entry.timer) return
    const wait = entry.last + ms - now
    if (wait <= 0) {
      entry.last = now
      fire(key)
      return
    }
    entry.timer = setTimeout(() => {
      entry.timer = null
      entry.last = Date.now()
      fire(key)
    }, wait)
  }
}
